/**
 * The earthworks site form, used full-page by New site and as the edit panel
 * on a site's own page (no modal: a site is a record).
 *
 * A blank field is sent as null (absent), never as 0. On edit only the keys
 * that changed are sent; PATCH keeps the rest. A site with jobs cannot move to
 * another customer — the API's 409 `site_has_jobs` is shown in the form.
 */
import React, { useMemo, useState } from 'react';
import ProvinceSelect from '../../components/ProvinceSelect';
import { blankToNull, toInput } from '../../lib/earthworks';
import type { EwCatalog, EwMeta, EwSite, EwSiteWrite } from '../../types/earthworks';
import { EW_INPUT, EW_PRIMARY, EW_SECONDARY, EwField, EwFormError } from './EwBits';

interface FormState {
  customer_id: string;
  kind: string;
  name: string;
  status: string;
  address: string;
  city: string;
  postal_code: string;
  legal_land: string;
  province_state: string;
  latitude: string;
  longitude: string;
  access_notes: string;
  notes: string;
}

const fromSite = (s: EwSite | null, defaultKind: string): FormState => ({
  customer_id: s?.customerId ?? '',
  kind: s?.kind ?? defaultKind,
  name: s?.name ?? '',
  status: s?.status ?? 'active',
  address: s?.address ?? '',
  city: s?.city ?? '',
  postal_code: s?.postalCode ?? '',
  legal_land: s?.legalLand ?? '',
  province_state: s?.provinceState ?? '',
  latitude: toInput(s?.latitude),
  longitude: toInput(s?.longitude),
  access_notes: s?.accessNotes ?? '',
  notes: s?.notes ?? '',
});

/** The write body: every field on create, only the changed ones on edit. */
export function siteWriteBody(form: FormState, initial: FormState | null): EwSiteWrite {
  const body: Record<string, unknown> = {};
  (Object.keys(form) as (keyof FormState)[]).forEach((k) => {
    if (initial && form[k] === initial[k]) return;
    const v = form[k];
    if (k === 'customer_id' || k === 'kind' || k === 'status') body[k] = v;
    else if (k === 'name') body[k] = v.trim();
    else body[k] = blankToNull(v);
  });
  if (!initial) Object.keys(body).forEach((k) => body[k] === null && delete body[k]);
  return body as EwSiteWrite;
}

export function EwSiteForm({
  site,
  catalog,
  meta,
  customers,
  pending,
  error,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  site: EwSite | null;
  catalog: EwCatalog | null;
  meta: EwMeta | null;
  customers: { id: string; name: string }[];
  pending: boolean;
  error: string | null;
  submitLabel: string;
  onSubmit: (body: EwSiteWrite) => void;
  onCancel: () => void;
}) {
  const kinds = catalog?.siteKinds ?? [];
  const initial = useMemo(() => (site ? fromSite(site, site.kind) : null), [site]);
  const [form, setForm] = useState<FormState>(() => fromSite(site, kinds[0]?.value ?? ''));
  const [localError, setLocalError] = useState<string | null>(null);
  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));
  const country = meta?.country === 'US' ? 'US' : 'CA';

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (!form.customer_id) return setLocalError('Pick the customer this site belongs to.');
    if (!form.kind) return setLocalError('Pick the kind of site.');
    if (!form.name.trim()) return setLocalError('Give the site a name.');
    const body = siteWriteBody(form, initial);
    if (initial && Object.keys(body).length === 0) return setLocalError('Nothing has changed.');
    onSubmit(body);
  };

  const text = (k: keyof FormState, label: string, hint?: string) => (
    <EwField label={label} hint={hint}>
      <input className={EW_INPUT} value={form[k]} onChange={(e) => set(k, e.target.value)} aria-label={label} />
    </EwField>
  );

  return (
    <form onSubmit={submit} className="space-y-4 px-4 py-4" data-testid="ew-site-form" noValidate>
      <EwFormError message={localError ?? error} />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <EwField label="Customer">
          <select className={EW_INPUT} value={form.customer_id} onChange={(e) => set('customer_id', e.target.value)} aria-label="Customer">
            <option value="">Select a customer…</option>
            {site && !customers.some((c) => c.id === site.customerId) && (
              <option value={site.customerId}>{site.customerName ?? '—'}</option>
            )}
            {customers.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </EwField>
        <EwField label="Kind">
          <select className={EW_INPUT} value={form.kind} onChange={(e) => set('kind', e.target.value)} aria-label="Kind">
            {!form.kind && <option value="">Select a kind…</option>}
            {kinds.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </EwField>
        {text('name', 'Name')}
        {text('address', 'Address')}
        {text('city', 'City')}
        {text('postal_code', country === 'US' ? 'ZIP code' : 'Postal code')}
        <EwField label={country === 'US' ? 'State' : 'Province'}>
          <ProvinceSelect
            country={country}
            value={form.province_state}
            onChange={(v) => set('province_state', v)}
            className={EW_INPUT}
            aria-label={country === 'US' ? 'State' : 'Province'}
          />
        </EwField>
        {text('legal_land', 'Legal land / LSD', 'For a lease or rural site')}
        <EwField label="Latitude">
          <input className={EW_INPUT} inputMode="decimal" value={form.latitude} onChange={(e) => set('latitude', e.target.value)} aria-label="Latitude" />
        </EwField>
        <EwField label="Longitude">
          <input className={EW_INPUT} inputMode="decimal" value={form.longitude} onChange={(e) => set('longitude', e.target.value)} aria-label="Longitude" />
        </EwField>
        {site && (
          <EwField label="Status">
            <select className={EW_INPUT} value={form.status} onChange={(e) => set('status', e.target.value)} aria-label="Status">
              {(catalog?.siteStatuses ?? []).map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </EwField>
        )}
      </div>
      <EwField label="Access notes">
        <textarea className={`${EW_INPUT} h-20 py-2`} value={form.access_notes} onChange={(e) => set('access_notes', e.target.value)} aria-label="Access notes" />
      </EwField>
      <EwField label="Notes">
        <textarea className={`${EW_INPUT} h-24 py-2`} value={form.notes} onChange={(e) => set('notes', e.target.value)} aria-label="Notes" />
      </EwField>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" className={EW_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="submit" className={EW_PRIMARY} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
